/**
 * Routine Card
 * Dashboard component to display todays routines
 */
import {
  Card,
  CardActionArea,
  CardActions,
  CardHeader,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';
import { CircleLoader } from '@components/Loaders';

import * as ROUTES from '@constants/routes';

import useRoutines from '@hooks/useRoutines';
import { useSession } from '@hooks/useSession';

import type { RoutineEvent } from '@models/routine';

const messages = defineMessages({
  showLess: {
    id: 'routineCard.showLess',
    defaultMessage: 'hide items',
  },
  moreItems: {
    id: 'routineCard.moreItems',
    defaultMessage: 'more items',
  },
});

interface RoutineCardProps {
  fixedWidth?: boolean;
  startTime: string;
  endTime?: string;
  routines: RoutineEvent[];
}

const RoutineCard: React.FC<RoutineCardProps> = ({
  startTime = '',
  endTime = '',
  routines = [],
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { formatMessage } = useIntl();
  const { completeRoutine, isRoutineComplete } = useRoutines();
  const {
    state: { activeProfile },
  } = useSession();
  const [user_id] = [activeProfile?.user_id];

  const theme = useTheme();

  async function onChangeStatus(routine: RoutineEvent) {
    if (!user_id) {
      return;
    }

    setLoading(true);
    await completeRoutine(routine);

    setLoading(false);
  }

  return (
    <Card
      sx={{
        my: '10px',
        minWidth: 200,
        boxShadow: `${theme.shadows[24]}`,
      }}
    >
      <CardHeader
        subheader={`${startTime}${endTime ? ` - ${endTime}` : ''}`}
        subheaderTypographyProps={{
          variant: 'body2',
          color: 'primary.main',
        }}
      />
      <Divider />
      <List sx={{ boxShadow: 'none' }}>
        {routines.slice(0, open ? undefined : 1).map((routine, index) => {
          const done = isRoutineComplete(routine);
          return (
            <ListItemButton
              key={index}
              divider
              onClick={() => {
                navigate(`${ROUTES.ROUTINES}${routine.id}`);
              }}
            >
              <ListItemText primary={routine.title} />
              <CircleLoader loading={loading} size="24px">
                <IconButton
                  color={
                    done
                      ? theme.palette.success.main
                      : theme.palette.text.secondary
                  }
                  name={done ? 'circle-check' : 'check'}
                  onClick={() => {
                    onChangeStatus(routines[index]);
                  }}
                  size={done ? 34 : 20}
                  iconType="fad"
                />
              </CircleLoader>
            </ListItemButton>
          );
        })}
      </List>
      {routines.length > 1 && (
        <CardActionArea
          onClick={() => setOpen(!open)}
          sx={{
            backgroundColor: theme.palette.info.light,
            cursor: 'pointer',
            borderRadius: '0',
            p: 1,
          }}
        >
          <CardActions>
            <Stack direction="row" spacing={2}>
              <Icon
                color="text.primary"
                name="chevron-down"
                rotation={open ? 180 : 0}
              />
              <Typography color="primary.main" variant="caption">
                {open
                  ? formatMessage(messages.showLess)
                  : `${routines.length - 1} ${formatMessage(
                      messages.moreItems
                    )}`}
              </Typography>
            </Stack>
          </CardActions>
        </CardActionArea>
      )}
    </Card>
  );
};

export default RoutineCard;
